/**
 * Language detection methods for ApiClient (mixin pattern).
 * These functions use `this` and are assigned to ApiClient.prototype.
 *
 * Per-format language detection is dispatched through the format registry.
 * Each format declares its own languageDetector capability and exports the
 * implementation; this file orchestrates the call but never branches on
 * format strings.
 */

import * as localDB from '../localDB';
import { API_BASE } from './helpers';
import { getFormatConfig } from '../formats/registry';

/**
 * ISBN Registration Group → language mapping.
 * Based on International ISBN Agency ranges.
 * Checks longest prefix first to handle multi-digit groups (e.g. 978-84 before 978-8).
 */
const ISBN_LANG_PREFIXES = [
    // 3-digit groups (check first)
    ['978-972', 'pt'], ['978-989', 'pt'], // Portugal
    ['978-957', 'zh'], ['978-986', 'zh'], // Taiwan
    ['978-979', 'id'], // Indonesia
    // 2-digit groups
    ['978-84', 'es'], ['978-85', 'pt'], ['978-86', 'sr'],
    ['978-87', 'da'], ['978-88', 'it'], ['978-89', 'ko'],
    ['978-90', 'nl'], ['978-91', 'sv'], ['978-92', 'en'], // international orgs
    ['978-93', 'hi'],
    // 1-digit groups
    ['978-0', 'en'], ['978-1', 'en'],
    ['978-2', 'fr'],
    ['978-3', 'de'],
    ['978-4', 'ja'],
    ['978-5', 'ru'],
    ['978-6', 'fa'], // approximate — covers Iran, Turkey, etc.
    ['978-7', 'zh'],
    // 979 prefix
    ['979-10', 'fr'], ['979-11', 'ko'], ['979-12', 'it'],
    ['979-0', 'en'], // music ISMNs, treat as English
    ['979-8', 'en'],
];

/**
 * Extract language from ISBN identifiers.
 * @param {string[]|undefined} identifiers - Array of dc:identifier values
 * @returns {string|null} ISO 639-1 language code or null
 */
function detectLangFromISBN(identifiers) {
    if (!identifiers?.length) return null;
    for (const raw of identifiers) {
        // Normalize: strip "urn:isbn:", "ISBN:", spaces, keep hyphens
        const cleaned = String(raw).replace(/^(urn:isbn:|isbn:?)\s*/i, '').replace(/\s/g, '');
        // Must look like an ISBN (10 or 13 digits, possibly with hyphens)
        if (!/^[\d-]{10,17}$/.test(cleaned)) continue;
        const digits = cleaned.replace(/-/g, '');
        if (digits.length !== 10 && digits.length !== 13) continue;

        // Convert ISBN-10 to ISBN-13 prefix for uniform matching
        const isbn13 = digits.length === 13 ? cleaned : `978-${cleaned}`;

        for (const [prefix, lang] of ISBN_LANG_PREFIXES) {
            if (isbn13.startsWith(prefix) || isbn13.replace(/-/g, '').startsWith(prefix.replace(/-/g, ''))) {
                return lang;
            }
        }
    }
    return null;
}

/**
 * Detect language from plain text.
 * Non-Latin scripts: Unicode character ranges.
 * Latin scripts: franc-min trigram detection with confidence check.
 * NOTE: This is a static method — does not use `this`.
 */
export async function detectLanguageFromText(text) {
    const sample = text.slice(0, 3000);
    let cjk = 0, hiragana = 0, katakana = 0, hangul = 0;
    let cyrillic = 0, arabic = 0, thai = 0, latin = 0;

    for (const ch of sample) {
        const cp = ch.codePointAt(0);
        if ((cp >= 0x4E00 && cp <= 0x9FFF) || (cp >= 0x3400 && cp <= 0x4DBF)) cjk++;
        else if (cp >= 0x3040 && cp <= 0x309F) hiragana++;
        else if (cp >= 0x30A0 && cp <= 0x30FF) katakana++;
        else if (cp >= 0xAC00 && cp <= 0xD7AF) hangul++;
        else if (cp >= 0x0400 && cp <= 0x04FF) cyrillic++;
        else if (cp >= 0x0600 && cp <= 0x06FF) arabic++;
        else if (cp >= 0x0E00 && cp <= 0x0E7F) thai++;
        else if ((cp >= 0x0041 && cp <= 0x007A) || (cp >= 0x00C0 && cp <= 0x024F)) latin++;
    }

    const total = cjk + hiragana + katakana + hangul + cyrillic + arabic + thai + latin;
    if (total === 0) return null;

    if (hiragana + katakana > total * 0.05) return 'ja';
    if (hangul > total * 0.1) return 'ko';
    if (cjk > total * 0.1) return 'zh';
    if (cyrillic > total * 0.3) return 'ru';
    if (arabic > total * 0.3) return 'ar';
    if (thai > total * 0.3) return 'th';

    // Latin: use franc-min with confidence check
    try {
        const { francAll } = await import('franc-min');
        const results = francAll(sample);
        if (!results.length || results[0][0] === 'und') return null;
        const [top, second] = results;
        if (second && top[1] - second[1] < 0.1) return null;

        const ISO3_TO_ISO1 = {
            eng: 'en', fra: 'fr', deu: 'de', spa: 'es', por: 'pt', ita: 'it',
            nld: 'nl', pol: 'pl', ron: 'ro', swe: 'sv', dan: 'da', nor: 'no',
            fin: 'fi', hun: 'hu', ces: 'cs', slk: 'sk', hrv: 'hr', srp: 'sr',
            rus: 'ru', jpn: 'ja', kor: 'ko', ara: 'ar', tha: 'th', cmn: 'zh',
            tur: 'tr', vie: 'vi', ind: 'id', hin: 'hi', ben: 'bn', urd: 'ur',
            fas: 'fa', heb: 'he', ell: 'el', ukr: 'uk', cat: 'ca', lat: 'la',
        };
        return ISO3_TO_ISO1[top[0]] ?? top[0].slice(0, 2);
    } catch {
        return null;
    }
}

/**
 * EPUB-specific language detection. Registered in the format registry as
 * formats.epub.languageDetector. Pure function: takes the file ArrayBuffer
 * and nothing else so it can be called from any context (no ApiClient
 * dependency).
 *
 * Signature `(file)` matches detectMobiLanguage after Phase F5.
 *
 * Order:
 *   1. ISBN prefix mapping (deterministic when present)
 *   2. dc:language from OPF metadata
 *   3. Re-read OPF identifiers for ISBN inference
 *   4. Sample chapter text and run franc-min via detectLanguageFromText
 */
export async function detectEpubLanguage(file) {
    // Import the EPUB metadata extractor here (instead of going through
    // the api mixin) so this function is callable from registry consumers
    // that don't have an ApiClient instance bound.
    const { parseMetadata } = await import('../epub/epubBookParser.js');

    // Wrap parseMetadata so the zip content sampling fallback (stages 3+4
    // below) still runs when metadata parsing fails. On EPUB_INVALID_ZIP
    // the file is unreadable at all → return null immediately; on
    // EPUB_INVALID_OPF the zip is fine but OPF is garbled, so we still
    // want to try sampling chapter HTML for language hints.
    //
    // Intentionally swallowed (documented absence): language detection is
    // best-effort and a null return is a legal "couldn't determine"
    // signal — the outer detectBookLanguage wrapper treats it as absence.
    // Per CLAUDE.md Bug Fix Philosophy, this is NOT a corruption
    // swallow: EPUB_INVALID_OPF still surfaces as a hard error at the
    // upload layer via bookManager.uploadBook's catch → EPUB_PARSE_FAILED.
    let meta;
    try {
        meta = await parseMetadata(file);
    } catch (e) {
        if (e?.code === 'EPUB_INVALID_ZIP') return null;
        // EPUB_INVALID_OPF and any other parseMetadata failure → fall
        // through to zip content sampling with empty metadata.
        meta = { identifiers: [], language: null };
    }

    // 1. ISBN prefix → language mapping
    let lang = detectLangFromISBN(meta.identifiers);

    // 2. dc:language from OPF metadata
    if (!lang) {
        const rawLang = meta.language?.split(/[-_]/)[0]?.toLowerCase() || null;
        lang = (rawLang && rawLang !== 'und' && rawLang !== 'undetermined') ? rawLang : null;
    }

    // 3+4. Sample chapter content
    if (!lang) {
        try {
            const JSZip = (await import('jszip')).default;
            const zip = await JSZip.loadAsync(file);

            const containerXml = await zip.file('META-INF/container.xml')?.async('text');
            if (!containerXml) return null;
            const parser = new DOMParser();
            const containerDoc = parser.parseFromString(containerXml, 'text/xml');
            const opfPath = containerDoc.querySelector('rootfile')?.getAttribute('full-path');
            if (!opfPath) return null;

            const opfContent = await zip.file(opfPath)?.async('text');
            if (!opfContent) return null;
            const opfDoc = parser.parseFromString(opfContent, 'text/xml');

            // 3. ISBN from OPF identifiers (some EPUBs differ from parseMetadata's path)
            if (!lang) {
                const ids = [];
                opfDoc.querySelectorAll('identifier').forEach(el => {
                    if (el.textContent) ids.push(el.textContent.trim());
                });
                lang = detectLangFromISBN(ids);
            }

            // 4. Sample chapter text → franc-min
            if (!lang) {
                const opfDir = opfPath.includes('/') ? opfPath.substring(0, opfPath.lastIndexOf('/') + 1) : '';
                const items = opfDoc.querySelectorAll('manifest item[media-type="application/xhtml+xml"]');

                let sampleText = '';
                for (const item of items) {
                    if (sampleText.length >= 5000) break;
                    const href = item.getAttribute('href');
                    if (!href) continue;
                    const fullPath = opfDir + decodeURIComponent(href);
                    const html = await zip.file(fullPath)?.async('text');
                    if (!html) continue;
                    const text = html
                        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                        .replace(/<[^>]+>/g, ' ')
                        .replace(/&\w+;/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim();
                    if (text.length < 50) continue;
                    sampleText += ' ' + text;
                }

                if (sampleText.length >= 50) {
                    lang = await detectLanguageFromText(sampleText);
                }
            }
        } catch (e) {
            console.warn('[detectEpubLanguage] content sampling failed:', e);
        }
    }

    return lang || null;
}

/**
 * Detect language for any book whose format declares a languageDetector
 * capability in the format registry. Updates localDB and returns the code.
 *
 * Mixin method — uses `this` (ApiClient instance) only for triggerSync.
 */
export async function detectBookLanguage(bookId) {
    try {
        const book = await localDB.getBook(bookId);
        if (!book) return null;
        // Skip if a usable language is already set
        if (book.language && book.language !== 'und' && book.language !== 'undetermined') return null;

        const config = getFormatConfig(book.file_type);
        if (!config?.languageDetector) return null;

        const file = await localDB.getBookFile(bookId);
        if (!file) return null;

        const mod = await config.languageDetector();
        // Unified signature (Phase F5): every format's language detector
        // takes only the file ArrayBuffer.
        const lang = await mod.detect(file);
        if (!lang) return null;

        // Store locally for offline display; do NOT write to reader_books.language
        // (auto-detected language is stored in canonical_books via backend upload flow)
        await localDB.updateBook(bookId, { language: lang });
        return lang;
    } catch (e) {
        console.warn('[detectBookLanguage] failed:', e);
        return null;
    }
}

/**
 * One-time backfill: for any book whose format has a metadataExtractor and
 * is missing a language, re-parse metadata from the local file and update
 * the record. Skips if already done.
 *
 * Mixin method — uses `this` only for triggerSync.
 */
export async function backfillBookLanguages(books) {
    const KEY = 'ev_lang_backfill_done';
    if (localStorage.getItem(KEY)) return;

    const candidates = (books || []).filter(b => {
        if (b.language) return false;
        return !!getFormatConfig(b.file_type)?.metadataExtractor;
    });
    if (candidates.length === 0) {
        localStorage.setItem(KEY, '1');
        return;
    }

    for (const book of candidates) {
        try {
            const file = await localDB.getBookFile(book.id);
            if (!file) continue;
            const config = getFormatConfig(book.file_type);
            const mod = await config.metadataExtractor();
            const meta = await mod.extract(file);
            if (!meta?.language) continue;
            // Normalize: "zh-cn" → "zh", "en-us" → "en"
            const lang = meta.language.split('-')[0];
            await localDB.updateBook(book.id, { language: lang });
        } catch { /* skip individual failures */ }
    }
    this.triggerSync();
    localStorage.setItem(KEY, '1');
}
